import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  MagnifyingGlass,
  PencilSimple,
  Plus,
  Trash,
  CheckCircle,
  WarningCircle,
  CircleWavy,
} from "phosphor-react";
import CustomPagination from "../../../Utilities/CustomPagination/CustomPagination.component";
import useStyles from "./Webhook.styles";
import AddOrEditWebhookModal from "./AddOrEditWebhookModal";
import { fdelete, fget } from "../../../../API/callsAPI";
import DateAndTimeViewer from "../../../Utilities/DateAndTimeViewer/DateAndTimeViewer.component";
import ChitranSvg from "../../../Utilities/Svg/ChitranSvg";
import DeleteModal from "../../../Utilities/DeleteModal/DeleteModal.component";

const WebhooksOverview = ({ projectId, setSnackValue }) => {
  const history = useHistory();
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useState("");
  const [webhookList, setWebhookList] = useState([]);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [eventsList, setEventsList] = useState([]);
  const [updateRefresh, setUpdateRefresh] = useState(0);
  const [addModalOpen, setAddModalOpen] = useState({
    open: false,
    data: "",
  });
  const [openDeleteModal, setOpenDeleteModal] = useState({
    open: false,
    data: {},
  });

  const handleRefreshCounter = () => {
    setUpdateRefresh((prev) => prev + 1);
  };

  async function getWebhooks() {
    setWebhookLoading(true);
    try {
      const res = await fget({
        url: `/webhooks/?project=${projectId}`,
      });
      if (res.status === 200) {
        setWebhookList(res.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setWebhookLoading(false);
    }
  }

  async function getWebhooksEvents() {
    try {
      const res = await fget({
        url: "/webhooks/events/",
      });
      if (res.status === 200) {
        setEventsList(res.data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function removeWebhook(id) {
    try {
      const res = await fdelete({
        url: `/webhooks/${id}/`,
      });
      if (res.status === 204) {
        handleRefreshCounter();
        setOpenDeleteModal({
          open: false,
          data: {},
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    if (projectId) {
      getWebhooks();
    }
  }, [projectId, updateRefresh]);

  useEffect(() => {
    getWebhooksEvents();
  }, []);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Typography
            sx={{ fontSize: "20px", fontWeight: 500, color: "#F9F9F9" }}
          >
            Webhooks
          </Typography>
          <Typography sx={{ color: "#6C65D9", fontSize: "20px" }}>
            {webhookList.count}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: "#252A38",
              borderRadius: "10px",
              p: "4px 10px",
              minWidth: "280px",
            }}
          >
            <MagnifyingGlass size={22} color="#F9F9F9" />
            <TextField
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search webhooks"
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{ sx: { color: "#F9F9F9", fontSize: "14px" } }}
              sx={{
                input: { color: "#F9F9F9" },
                fieldset: { border: "none" },
                backgroundColor: "transparent",
              }}
            />
          </Box>

          <Button
            startIcon={<Plus size={20} weight="bold" />}
            variant="contained"
            disableElevation
            onClick={() =>
              setAddModalOpen({
                open: true,
                data: "",
              })
            }
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              minWidth: "170px",
              backgroundColor: "#6C65D9",
              padding: "10px 20px",
              height: "42px",
              boxSizing: "border-box",
            }}
          >
            New Webhook
          </Button>
        </Box>
      </Box>

      <Typography sx={{ color: "#B1B5C9", fontSize: "14px" }}>
        Webhooks allow external services to be notified when certain events
        happen. When the specific events happen, we’ll send a POST request to
        each of the URLs you provide.
      </Typography>
      {webhookLoading && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            marginTop: "200px",
          }}
        >
          <CircularProgress />
        </div>
      )}
      {!webhookLoading && webhookList.count === 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            width: "100%",
            height: "100%",
            gap: "18px",
            marginTop: "24px",
          }}
        >
          <ChitranSvg />
          <Typography>
            No webhook found, create one by clicking on New Webhook button
          </Typography>
        </div>
      )}
      {!webhookLoading && webhookList.count > 0 && (
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader} align="left">
                  Payload URL
                </TableCell>
                <TableCell className={classes.tableHeader} align="center">
                  Name
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  Created by
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  Created on
                </TableCell>
                <TableCell align="center" className={classes.tableHeader}>
                  Status
                </TableCell>
                <TableCell align="right" className={classes.tableHeader}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {webhookList.results
                .filter((webhook) =>
                  webhook.name
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()),
                )
                .map((webhook) => {
                  return (
                    <TableRow
                      key={webhook.id}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.04)",
                        },
                      }}
                      onClick={() => {
                        history.push(
                          `/${projectId}/settings/webhooks/${webhook.id}`,
                        );
                      }}
                    >
                      <TableCell
                        align="left"
                        sx={{
                          color: "#F9F9F9",
                          fontSize: "14px",
                          maxWidth: "340px",
                          wordBreak: "break-all",
                          borderColor: "#606479",
                        }}
                      >
                        {webhook.url}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: "#F9F9F9",
                          fontSize: "14px",
                          borderColor: "#606479",
                        }}
                      >
                        {webhook.name}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: "#F9F9F9",
                          fontSize: "14px",
                          borderColor: "#606479",
                        }}
                      >
                        {webhook.createdBy}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          color: "#F9F9F9",
                          fontSize: "14px",
                          borderColor: "#606479",
                        }}
                      >
                        <DateAndTimeViewer dateAndTime={webhook.created_at} />
                      </TableCell>
                      <TableCell align="center" sx={{ borderColor: "#606479" }}>
                        {webhook.is_active ? (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 1,
                              padding: "4px 8px",
                              borderRadius: "100px",
                              backgroundColor: "#65D98429",
                              border: `1px solid #65D984`,
                              fontSize: "12px",
                              color: "#f9f9f9",
                            }}
                          >
                            Active
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 1,
                              padding: "4px 8px",
                              borderRadius: "100px",
                              backgroundColor: "#7B7B7B29",
                              border: `1px solid #7B7B7B`,
                              fontSize: "12px",
                              color: "#f9f9f9",
                            }}
                          >
                            InActive
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ borderColor: "#606479" }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: 1,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setAddModalOpen({
                              open: true,
                              data: webhook,
                            });
                          }}
                        >
                          <IconButton disabled>
                            <PencilSimple size={22} color="#F9F9F9" />
                          </IconButton>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDeleteModal({
                                open: true,
                                data: webhook,
                              });
                            }}
                          >
                            <Trash size={22} color="#D61F1F" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* <footer style={{ bottom: "0", height: "100%" }}>
        <CustomPagination
          handlePageChange={() => console.log("do nothing")}
          currentPage={0}
          totalItem={100}
          totalPage={Math.ceil(100 / 50)}
          setIsNewPageLoading={false}
          trackable="Webhooks"
        />
      </footer> */}

      <Modal
        open={addModalOpen.open}
        onClose={() =>
          setAddModalOpen({
            open: false,
            data: "",
          })
        }
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <AddOrEditWebhookModal
          handleClose={() =>
            setAddModalOpen({
              open: false,
              data: "",
            })
          }
          setSnackValue={setSnackValue}
          projectId={projectId}
          eventsList={eventsList}
          handleRefreshCounter={handleRefreshCounter}
          editData={addModalOpen.data}
        />
      </Modal>
      <Modal
        open={openDeleteModal.open}
        onClose={() =>
          setOpenDeleteModal({
            open: false,
            data: {},
          })
        }
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <DeleteModal
          name={openDeleteModal.data?.name}
          type="webhook"
          handleCloseDeleteModal={() =>
            setOpenDeleteModal({
              open: false,
              data: {},
            })
          }
          handleDelete={() => removeWebhook(openDeleteModal.data.id)}
        />
      </Modal>
    </Box>
  );
};

export default WebhooksOverview;
